import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import axios from "axios";
import { BbotModal, Button, Select } from "top-component-library";
import { generalErrorAlert } from "util/Utils";

// The status arrays come back flat: [title, name, status, name, status, ...].
const parseStatusList = (list) => {
  if (!list || list.length === 0) return { title: "", rows: [] };
  const [title, ...rest] = list;
  const rows = [];
  for (let i = 0; i < rest.length; i += 2) {
    rows.push({ name: rest[i], status: rest[i + 1] ?? "" });
  }
  return { title, rows };
};

const StatusList = ({ list }) => {
  const { title, rows } = parseStatusList(list);
  if (!title && rows.length === 0) return null;
  return (
    <div className="margin-bottom-3">
      <h5>{title}</h5>
      {rows.map((row, index) => (
        <div key={`${row.name}-${index}`} className="margin-bottom-2">
          <div>{row.name}</div>
          <div style={{ color: row.status.includes("normal") ? "#237804" : "#cf1322" }}>{row.status}</div>
        </div>
      ))}
    </div>
  );
};

StatusList.propTypes = { list: PropTypes.arrayOf(PropTypes.string) };

// Read-only status viewer: pick a location, then show its station and menu
// statuses (a status containing "normal" is healthy).
const CheckMenuStatusModal = ({ visible, onClose }) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [statuses, setStatuses] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setStatuses(null);
    const loadLocations = async () => {
      try {
        const res = await axios.get("/owner/getAllLocations");
        const sorted = [...(res.data.locations || [])].sort((a, b) =>
          (a.locationShortId || "").localeCompare(b.locationShortId || "")
        );
        setLocations(sorted);
        setSelectedLocationId(sorted[0]?.locationId ?? null);
      } catch (error) {
        generalErrorAlert(error, "Error retrieving locations, please try again.");
      }
    };
    loadLocations();
  }, [visible]);

  const checkStatus = async () => {
    if (!selectedLocationId) return;
    setIsChecking(true);
    try {
      const res = await axios.post("/api/checkMenuStatus", { locationId: selectedLocationId });
      setStatuses(res.data.statuses);
    } catch (error) {
      generalErrorAlert(error, "Error retrieving menu status, please try again.");
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <BbotModal visible={visible} onCancel={onClose} title="Check Menu Status" footer={null}>
      <div className="margin-bottom-2">
        <strong>Choose a Location:</strong>
        <div style={{ color: "var(--color-neutral__text-secondary)" }}>
          Use the dropdown to search for location names, or choose one from the list
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Select
          showSearch
          style={{ flex: 1 }}
          placeholder="Select a location"
          value={selectedLocationId}
          onChange={setSelectedLocationId}
          filterOption={(input, option) => (option.children || "").toLowerCase().includes(input.toLowerCase())}
        >
          {locations.map((location) => (
            <Select.Option key={location.locationId} value={location.locationId}>
              {location.locationShortId}
            </Select.Option>
          ))}
        </Select>
        <Button type="primary" loading={isChecking} onClick={checkStatus}>
          Check
        </Button>
      </div>
      {statuses && (
        <div>
          <StatusList list={statuses.stationStatuses} />
          <StatusList list={statuses.menuStatuses} />
        </div>
      )}
    </BbotModal>
  );
};

CheckMenuStatusModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default CheckMenuStatusModal;
